import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AvatarIniciales,
  CeldaPrincipal,
  ChipsSeleccion,
  DataTable,
  Icon,
  KpiCard,
  KpiGrid,
  TextoTruncado,
  Tip,
} from "@/components/ds";
import {
  FormDrawer,
  FormDrawerClose,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerFooterHint,
  FormDrawerHeader,
  FormDrawerTitle,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, Loader2, Plus, Save, Search } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea } from "@/lib/area";
import type { AreaSummary } from "@/types/area";
import type { Trabajador } from "@/types/trabajador";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";

// ─── Vocabulario ─────────────────────────────────────────────────────────────

interface Cargo {
  id: number;
  nombre: string;
  descripcion: string | null;
  eliminado_at: string | null;
  personas_count: number;
  areas: AreaSummary[];
}

/**
 * Las cuatro vistas del listado. `papelera` NO es un filtro más del catálogo:
 * es otro conjunto, que se le pide al servidor con `en_papelera=1`. Los otros
 * tres se pliegan del mismo catálogo activo, así que el número del riel y el de
 * la tarjeta no pueden discrepar.
 */
type Vista = "todos" | "sin_trabajadores" | "sin_descripcion" | "papelera";

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function normalizar(s: string): string {
  return s.trim().toLowerCase();
}

// ─── Celdas ──────────────────────────────────────────────────────────────────

/**
 * Dotación del cargo: cuántos trabajadores tiene y qué parte del total
 * asignado representa.
 *
 * La barra se dibuja acá y NO con `ProgressBar` del DS a propósito. Esa barra
 * colorea por umbral (>80% verde, <50% rojo) porque mide CUMPLIMIENTO; acá el
 * número es un reparto, y un cargo que concentra el 90% de la gente no es
 * "verde" ni uno con el 10% es "rojo". El color de marca sólo da la magnitud.
 */
function CeldaDotacion({ n, total }: { n: number; total: number }) {
  const pct = total > 0 ? Math.round((n / total) * 100) : 0;

  return (
    // `w-full` no es decorativo: la celda del DataTable es un flex, y una
    // columna sin ancho declarado resuelve al ancho de su CONTENIDO — que acá
    // es la línea "14 23%". Sin esto la barra mide 50px en vez de la columna,
    // y deja de ser comparable entre filas, que es lo único que hace.
    <div className="flex w-full min-w-0 flex-col gap-1 pr-3">
      <span className="flex items-baseline gap-1.5">
        <span className="fc-num text-[13px]">{n}</span>
        <span className="text-soft text-[10.5px]">{total > 0 ? `${pct}%` : "—"}</span>
      </span>
      <div
        className="bg-border-subtle h-1 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={n}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${plural(n, "trabajador", "trabajadores")} de ${total} asignados`}
      >
        <div className="bg-primary h-full rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function EstadoBadge({ c }: { c: Cargo }) {
  if (c.eliminado_at) {
    return (
      <Badge variant="muted" dot>
        En papelera
      </Badge>
    );
  }
  return (
    <Badge variant={c.personas_count > 0 ? "success" : "neutral"} dot>
      {c.personas_count > 0 ? "En uso" : "Sin trabajadores"}
    </Badge>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export function Cargos() {
  /** El catálogo vivo: todo lo que NO está en la papelera. */
  const [catalogo, setCatalogo] = useState<Cargo[]>([]);
  const [papelera, setPapelera] = useState<Cargo[]>([]);
  const [areas, setAreas] = useState<AreaSummary[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>("todos");
  const [search, setSearch] = useState("");
  const [areaId, setAreaId] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editando, setEditando] = useState<Cargo | null>(null);

  // Mover a papelera (DELETE → soft delete)
  const [aEliminar, setAEliminar] = useState<Cargo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Restaurar desde papelera
  const [aRestaurar, setARestaurar] = useState<Cargo | null>(null);
  const [restaurando, setRestaurando] = useState(false);

  // Purgar (hard delete)
  const [aPurgar, setAPurgar] = useState<Cargo | null>(null);
  const [purgando, setPurgando] = useState(false);
  const [confirmPurgarTexto, setConfirmPurgarTexto] = useState("");

  /**
   * Padrón de trabajadores, sólo para ponerle cara a la dotación del cargo que
   * se está editando. Se pide UNA vez, la primera vez que se abre una ficha, y
   * no al montar la pantalla: el listado no lo necesita para nada y en una
   * empresa grande es la request más cara de las cuatro.
   */
  const [roster, setRoster] = useState<Trabajador[] | null>(null);
  const rosterPedido = useRef(false);

  const pedirRoster = useCallback(async () => {
    if (rosterPedido.current) return;
    rosterPedido.current = true;
    try {
      const res = await api.get<{ data: Trabajador[] }>("/personas?all=1");
      setRoster(res.data);
    } catch {
      // Silencioso a propósito: los avatares acompañan a un número que el cargo
      // ya trae. Un toast de error acá sólo distraería de lo que la persona vino
      // a hacer, que es editar el cargo.
      setRoster([]);
    }
  }, []);

  // --- Guía piloto de Cargos ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Se usa para pasar modal={false} al FormDrawer durante esos pasos,
   * evitando que el focus trap de Radix bloquee los botones Siguiente/Anterior
   * del popover de driver.js (que se renderizan fuera del DOM del diálogo).
   *
   * En el flujo normal (sin guía) el diálogo sigue siendo modal={true} por defecto.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   * Se necesita para no tener efectos secundarios si el componente desmonta.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog (estables entre renders).
   *
   * Paso 2 — Highlight del botón Nuevo cargo:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default (~10000).
   *   - onNextClick: abre el diálogo, activa modal={false} en el dialog, hace poll
   *     rAF para esperar que monte [data-tour="cargo-nombre"], luego avanza.
   *
   * Pasos 3–5 — Campos dentro del diálogo:
   *   - driver está en z default (~10000), por encima del Radix Dialog (z-50).
   *   - driver-fc-dialog-step NO está en el body → overlay de driver es visible y
   *     el cutout expone el campo seleccionado.
   *   - Radix Dialog tiene modal={false} → focus trap desactivado →
   *     botones Next/Prev de driver son clickeables.
   *
   * Paso 6 — Popover de cierre:
   *   - Desactiva modal={false}, el diálogo puede cerrarse normalmente.
   */
  const buildCargosGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "¿Por qué crear cargos?",
        description:
          "Sin cargos definidos no podrás asignar puestos a tus trabajadores ni emitir documentos por función. Creemos el primero.",
      },
    },
    // Paso 2 — Highlight del botón Nuevo cargo (pre-diálogo)
    // driver-fc-dialog-step body class está activa → driver z-index bajo (40/45) bajo Radix
    {
      element: '[data-tour="cargos-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        // Removemos la clase antes de los pasos in-dialog para que driver
        // vuelva a su z default (~10000) y pueda resaltar los campos dentro del diálogo.
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Crear un cargo",
        description:
          "Acá defines un cargo para tu empresa. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Abre el diálogo y desactiva el focus trap para que driver mantenga
          // el control. El poll rAF espera a que monte el primer campo antes
          // de avanzar, evitando que driver intente resaltar un nodo inexistente.
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            if (document.querySelector('[data-tour="cargo-nombre"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              // Fallback: avanzamos igual para no dejar la guía atascada.
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Campo Nombre (dentro del diálogo, primer campo visible)
    {
      element: '[data-tour="cargo-nombre"]',
      popover: {
        title: "Nombre del cargo",
        description:
          "Asigna un nombre claro al cargo, por ejemplo «Supervisor de Terreno». Este nombre aparecerá en los documentos del sistema de prevención.",
      },
    },
    // Paso 4 — Campo Descripción (dentro del diálogo, driver en z default)
    {
      element: '[data-tour="cargo-descripcion"]',
      popover: {
        title: "Descripción del cargo",
        description:
          "Describe las tareas reales del cargo con el mayor detalle posible — esta información alimenta el sistema de prevención de riesgos.",
      },
    },
    // Paso 5 — Campo Áreas (dentro del diálogo)
    {
      element: '[data-tour="cargo-areas"]',
      popover: {
        title: "Áreas del cargo",
        description:
          "Asocia el cargo a las áreas donde aplica. Puedes seleccionar más de una.",
      },
    },
    // Paso 6 — Botón Guardar (dentro del diálogo)
    {
      element: '[data-tour="cargo-guardar"]',
      popover: {
        title: "Guardar cargo",
        description:
          "Cuando termines de completar los datos, guarda para crear el cargo.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Al salir de los pasos in-dialog, volvemos al comportamiento modal normal.
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 7 — Popover de cierre/confirmación (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en «Abrir guía» en el panel de primeros pasos, o en el botón de ayuda de esta página.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-cargos", { buildSteps: buildCargosGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-cargos", startGuide, { anchorSelector: '[data-tour="cargos-crear"]' });
  useGuideAutostart("guide-cargos", startGuide, { anchorSelector: '[data-tour="cargos-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía piloto ---

  /**
   * Catálogo, papelera y áreas se piden JUNTOS. Los contadores del riel tienen
   * que estar bien desde el primer pintado, y el catálogo de áreas lo necesitan
   * tanto el filtro de la barra como los chips de la ficha — pedirlo una vez acá
   * ahorra una request por cada apertura del formulario.
   */
  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [cat, pap, ars] = await Promise.all([
        api.get<{ data: Cargo[] }>("/cargos?all=1"),
        api.get<{ data: Cargo[] }>("/cargos?all=1&en_papelera=1"),
        api.get<{ data: AreaSummary[] }>("/areas?all=1"),
      ]);
      setCatalogo(cat.data);
      setPapelera(pap.data);
      setAreas(ars.data);
    } catch {
      setLoadError("No se pudo cargar el catálogo de cargos.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ── Mover a papelera (DELETE → soft delete) ───────────────────────────────
  const moverAPapelera = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/cargos/${aEliminar.id}`);
      toast.success(
        `"${aEliminar.nombre}" se movió a la papelera. Puedes restaurarlo cuando quieras.`,
      );
      setAEliminar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este cargo ya está en la papelera.");
      } else {
        toast.error("No se pudo mover el cargo a la papelera.");
      }
    } finally {
      setEliminando(false);
    }
  };

  // ── Restaurar desde papelera ──────────────────────────────────────────────
  const restaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      await api.post(`/cargos/${aRestaurar.id}/restaurar`, {});
      toast.success(`"${aRestaurar.nombre}" volvió al catálogo.`);
      setARestaurar(null);
      cargar();
    } catch (err) {
      // El servidor rechaza restaurar cuando ya existe un cargo ACTIVO con ese
      // nombre: el nombre es único por empresa, papelera incluida.
      if (err instanceof ApiError && err.status === 422) {
        toast.error(
          "Ya existe un cargo activo con ese nombre. Renombra el otro antes de restaurar este.",
        );
      } else {
        toast.error("No se pudo restaurar el cargo.");
      }
    } finally {
      setRestaurando(false);
    }
  };

  // ── Purgar (hard delete, irreversible) ────────────────────────────────────
  const purgar = async () => {
    if (!aPurgar) return;
    setPurgando(true);
    try {
      await api.delete(`/cargos/${aPurgar.id}/purgar`);
      toast.success(`"${aPurgar.nombre}" se eliminó definitivamente.`);
      setAPurgar(null);
      setConfirmPurgarTexto("");
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Solo se puede purgar un cargo desde la papelera.");
      } else {
        toast.error("No se pudo purgar el cargo.");
      }
      setAPurgar(null);
      setConfirmPurgarTexto("");
    } finally {
      setPurgando(false);
    }
  };

  // ── Derivados ─────────────────────────────────────────────────────────────

  const sinTrabajadores = useMemo(
    () => catalogo.filter((c) => c.personas_count === 0),
    [catalogo],
  );
  const sinDescripcion = useMemo(
    () => catalogo.filter((c) => !c.descripcion?.trim()),
    [catalogo],
  );

  /**
   * Denominador de la columna Dotación: la gente REPARTIDA en cargos, no la
   * dotación de la empresa. Un trabajador sin cargo no está repartido en
   * ninguno, así que sumarlo al denominador dejaría los porcentajes de la
   * columna sin cerrar en 100%.
   */
  const totalAsignados = useMemo(
    () => catalogo.reduce((suma, c) => suma + c.personas_count, 0),
    [catalogo],
  );
  const conTrabajadores = catalogo.length - sinTrabajadores.length;

  const filas = useMemo(() => {
    const base =
      vista === "todos"
        ? catalogo
        : vista === "sin_trabajadores"
          ? sinTrabajadores
          : vista === "sin_descripcion"
            ? sinDescripcion
            : papelera;

    const q = normalizar(search);
    const area = areaId ? Number(areaId) : null;

    return base.filter((c) => {
      if (area !== null && !c.areas.some((a) => a.id === area)) return false;
      if (!q) return true;
      return (
        c.nombre.toLowerCase().includes(q) || (c.descripcion ?? "").toLowerCase().includes(q)
      );
    });
  }, [vista, catalogo, sinTrabajadores, sinDescripcion, papelera, search, areaId]);

  const esPapelera = vista === "papelera";
  const hayFiltro = search.trim() !== "" || areaId !== "";

  const mensajeVacio = hayFiltro
    ? "Ningún cargo coincide con los filtros."
    : esPapelera
      ? "La papelera está vacía."
      : vista === "sin_trabajadores"
        ? "Todos los cargos tienen al menos un trabajador."
        : vista === "sin_descripcion"
          ? "Todos los cargos tienen descripción."
          : 'Todavía no defines cargos. Haz clic en "Nuevo cargo" para empezar.';

  const limpiarFiltros = () => {
    setSearch("");
    setAreaId("");
  };

  /**
   * Nombres ya tomados, catálogo Y papelera. La regla `unique` de
   * `CargoRequest` consulta la tabla cruda, así que un cargo en la papelera
   * sigue ocupando su nombre — avisarlo antes evita un 422 desconcertante.
   */
  const nombresUsados = useMemo(
    () =>
      [...catalogo, ...papelera].map((c) => ({
        id: c.id,
        nombre: c.nombre,
        enPapelera: c.eliminado_at !== null,
      })),
    [catalogo, papelera],
  );

  const abrirEdicion = (c: Cargo) => {
    setEditando(c);
    pedirRoster();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cargos"
        subtitle="Cada trabajador tiene un cargo, y por cargo se agrupan las capacitaciones."
        rightContent={
          <Button data-tour="cargos-crear" onClick={() => setCreateOpen(true)} className="shrink-0">
            <Plus className="h-4 w-4" />
            Nuevo cargo
          </Button>
        }
      />

      {/*
        modal={false} desactiva el focus trap de Radix Dialog durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el diálogo es modal.
      */}
      <FormDrawer
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setGuideInDialog(false);
          setCreateOpen(open);
        }}
        side="right"
        modal={guideInDialog ? false : undefined}
        dismissible={guideInDialog ? false : undefined}
      >
        <CargoDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          areas={areas}
          nombresUsados={nombresUsados}
          roster={roster}
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            cargar();
            invalidate();
          }}
        />
      </FormDrawer>

      <KpiGrid min={210}>
        <KpiCard
          label="Cargos definidos"
          valor={catalogo.length}
          detalle={`${conTrabajadores} con gente · ${sinTrabajadores.length} sin nadie`}
        />
        <KpiCard
          label="Trabajadores asignados"
          valor={totalAsignados}
          detalle={`repartidos en ${plural(conTrabajadores, "cargo", "cargos")}`}
        />
        <KpiCard
          label="Sin trabajadores"
          valor={sinTrabajadores.length}
          detalle={`de ${plural(catalogo.length, "cargo definido", "cargos definidos")}`}
        />
      </KpiGrid>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">{loadError}</p>
            <button type="button" onClick={cargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={filas}
          getRowKey={(c) => c.id}
          // 778px de columnas (240+170+168+124+76) + 28px del `px-3.5` de la
          // fila. Por debajo de esa suma la grilla se desborda del contenedor
          // en vez de scrollear, y la cabecera deja de alinearse con las filas.
          minWidth={806}
          pageSize={20}
          onRowClick={esPapelera ? undefined : abrirEdicion}
          emptyMessage={mensajeVacio}
          emptyIcon={hayFiltro ? "search_off" : esPapelera ? "delete" : "badge"}
          emptyAction={
            hayFiltro ? (
              <Button variant="outline" size="sm" onClick={limpiarFiltros}>
                Quitar filtros
              </Button>
            ) : undefined
          }
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => {
                  if (!v) return;
                  setVista(v as Vista);
                  setSearch("");
                }}
              >
                <ToggleGroupItem value="todos">
                  Todos
                  <ToggleGroupCount>{catalogo.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="sin_trabajadores">
                  Sin trabajadores
                  <ToggleGroupCount>{sinTrabajadores.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="sin_descripcion">
                  Sin descripción
                  <ToggleGroupCount>{sinDescripcion.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="papelera">
                  <Icon name="delete" size={16} />
                  Papelera
                  <ToggleGroupCount>{papelera.length}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por nombre o descripción…"
                  className="h-9 pl-8 text-[13px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Las áreas son un clasificador OPCIONAL: si la empresa no las
                  usa, el filtro no aparece en vez de ofrecer un select vacío. */}
              {areas.length > 0 && (
                <NativeSelect
                  aria-label="Filtrar por área"
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value)}
                >
                  <NativeSelectOption value="">Todas las áreas</NativeSelectOption>
                  {areas.map((a) => (
                    <NativeSelectOption key={a.id} value={String(a.id)}>
                      {formatNombreArea(a)}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              )}
            </>
          }
          columns={[
            {
              key: "cargo",
              header: "Cargo",
              width: "minmax(240px,2fr)",
              cell: (c) => (
                <CeldaPrincipal
                  titulo={c.nombre}
                  meta={c.descripcion?.trim() || "Sin descripción"}
                />
              ),
            },
            {
              key: "areas",
              header: "Áreas habituales",
              width: "minmax(170px,1.2fr)",
              cell: (c) => (
                <TextoTruncado className="block truncate pr-3 text-[12.5px]">
                  {c.areas.length > 0
                    ? c.areas.map(formatNombreArea).join(" · ")
                    : "Sin área habitual"}
                </TextoTruncado>
              ),
            },
            {
              key: "dotacion",
              header: "Dotación",
              width: "168px",
              cell: (c) => <CeldaDotacion n={c.personas_count} total={totalAsignados} />,
            },
            {
              key: "estado",
              header: "Estado",
              width: "124px",
              cell: (c) => <EstadoBadge c={c} />,
            },
            {
              // Sin encabezado y NO `srOnlyHeader`: `sr-only` es
              // `position:absolute`, así que sobre una celda de la grilla la
              // saca del flujo y corre las columnas siguientes — ver el
              // comentario en DataTable.
              key: "acciones",
              header: "",
              width: "76px",
              align: "right",
              cell: (c) =>
                esPapelera ? (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Devolver al catálogo">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Restaurar ${c.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setARestaurar(c);
                        }}
                      >
                        <Icon name="restore_from_trash" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Eliminar definitivamente">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Eliminar definitivamente ${c.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAPurgar(c);
                          setConfirmPurgarTexto("");
                        }}
                      >
                        <Icon name="delete_forever" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Editar cargo">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${c.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirEdicion(c);
                        }}
                      >
                        <Icon name="edit" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Mover a la papelera">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Mover ${c.nombre} a la papelera`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAEliminar(c);
                        }}
                      >
                        {/* `delete`, no `delete_outline`: en Material Symbols
                            el relleno es un EJE (FILL), no un nombre distinto,
                            y la app carga la fuente con FILL@0. */}
                        <Icon name="delete" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ),
            },
          ]}
        />
      )}

      <FormDrawer
        open={editando !== null}
        onOpenChange={(open) => !open && setEditando(null)}
        side="right"
      >
        {editando && (
          <CargoDrawer
            key={`edit-${editando.id}`}
            mode="edit"
            cargo={editando}
            areas={areas}
            nombresUsados={nombresUsados}
            roster={roster}
            onSaved={() => {
              setEditando(null);
              cargar();
            }}
            onPedirBorrar={() => {
              const objetivo = editando;
              setEditando(null);
              setAEliminar(objetivo);
            }}
          />
        )}
      </FormDrawer>

      {/* Confirmar: mover a papelera */}
      <AlertDialog open={aEliminar !== null} onOpenChange={(open) => !open && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aEliminar ? (
                  <div className="space-y-2">
                    <p>
                      ¿Quieres mover el cargo <strong>{aEliminar.nombre}</strong> a la papelera?
                    </p>
                    {aEliminar.personas_count > 0 ? (
                      <p>
                        Sus{" "}
                        <strong>
                          {plural(aEliminar.personas_count, "trabajador", "trabajadores")}
                        </strong>{" "}
                        van a aparecer <strong>sin cargo</strong> mientras esté en la papelera. Si
                        lo restauras, lo recuperan tal como estaba.
                      </p>
                    ) : (
                      <p>
                        Sale del catálogo, pero puedes{" "}
                        <strong>restaurarlo en cualquier momento</strong> desde la pestaña Papelera.
                      </p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      El nombre sigue reservado mientras esté en la papelera: no vas a poder crear
                      otro cargo que se llame igual.
                    </p>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                moverAPapelera();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: restaurar */}
      <AlertDialog open={aRestaurar !== null} onOpenChange={(open) => !open && setARestaurar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar cargo</AlertDialogTitle>
            <AlertDialogDescription>
              {aRestaurar ? (
                <>
                  ¿Restaurar <strong>{aRestaurar.nombre}</strong>? Vuelve al catálogo y sus
                  trabajadores recuperan el cargo que tenían.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                restaurar();
              }}
              disabled={restaurando}
            >
              {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: purgar (typed-confirm) */}
      <AlertDialog
        open={aPurgar !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAPurgar(null);
            setConfirmPurgarTexto("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Eliminar permanentemente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aPurgar ? (
                  <div className="space-y-3">
                    <p>
                      Estás por eliminar definitivamente el cargo <strong>{aPurgar.nombre}</strong>.
                      Esta acción es <strong>irreversible</strong>, y los trabajadores que lo tenían
                      quedan sin cargo de forma permanente.
                    </p>
                    <p className="font-medium">
                      Escribe <span className="fc-num text-destructive">ELIMINAR</span> para
                      confirmar:
                    </p>
                    <Input
                      value={confirmPurgarTexto}
                      onChange={(e) => setConfirmPurgarTexto(e.target.value)}
                      placeholder="ELIMINAR"
                      className="fc-num"
                      disabled={purgando}
                      autoFocus
                    />
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgando}
              onClick={() => {
                setAPurgar(null);
                setConfirmPurgarTexto("");
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                purgar();
              }}
              disabled={purgando || confirmPurgarTexto !== "ELIMINAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Ficha del cargo (drawer) ────────────────────────────────────────────────

interface NombreUsado {
  id: number;
  nombre: string;
  enPapelera: boolean;
}

interface CargoDrawerProps {
  mode: "create" | "edit";
  cargo?: Cargo;
  areas: AreaSummary[];
  nombresUsados: NombreUsado[];
  /** Padrón para ponerle cara a la dotación. `null` mientras se está pidiendo. */
  roster: Trabajador[] | null;
  onSaved: () => void;
  onPedirBorrar?: () => void;
}

/** Cuántos avatares entran antes de resumir el resto en un "+N". */
const AVATARES_VISIBLES = 8;

function CargoDrawer({
  mode,
  cargo,
  areas,
  nombresUsados,
  roster,
  onSaved,
  onPedirBorrar,
}: CargoDrawerProps) {
  const [nombre, setNombre] = useState(cargo?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(cargo?.descripcion ?? "");
  const [areasIds, setAreasIds] = useState<number[]>(cargo?.areas.map((a) => a.id) ?? []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const esNuevo = mode === "create";
  const limpio = nombre.trim();

  /**
   * Colisión de nombre. Se corta acá y no sólo en el estilo del botón: el
   * nombre es único por empresa CON la papelera adentro, así que guardar
   * devolvería un 422 cuya respuesta el formulario ya conoce.
   */
  const choque = useMemo(() => {
    if (!limpio) return null;
    return (
      nombresUsados.find(
        (n) => n.id !== cargo?.id && normalizar(n.nombre) === normalizar(limpio),
      ) ?? null
    );
  }, [limpio, nombresUsados, cargo?.id]);

  /**
   * Trabajadores del cargo. Sale del padrón ya cargado — el `personas_count`
   * del cargo sigue siendo la cifra oficial, esto sólo le pone cara.
   */
  const trabajadores = useMemo(() => {
    if (esNuevo || !cargo || !roster) return [];
    return roster.filter((t) => t.cargo?.id === cargo.id);
  }, [esNuevo, cargo, roster]);

  const puedeGuardar = limpio !== "" && choque === null && !isSubmitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeGuardar) return;

    const payload = {
      nombre: limpio,
      descripcion: descripcion.trim() || null,
      areas_ids: areasIds,
    };

    setIsSubmitting(true);
    try {
      if (esNuevo) {
        await api.post("/cargos", payload);
        toast.success(`Cargo "${limpio}" creado. Ya puedes asignarle trabajadores.`);
      } else if (cargo) {
        await api.put(`/cargos/${cargo.id}`, payload);
        toast.success(`Cambios guardados en "${limpio}".`);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const primero = Object.values(err.errors)[0]?.[0];
        toast.error(primero ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo guardar el cargo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Qué falta ANTES de intentar guardar, en vez de dejar el botón inerte. */
  const hint = choque
    ? choque.enPapelera
      ? "Ese nombre lo ocupa un cargo en la papelera."
      : "Ese cargo ya existe: cambia el nombre."
    : !limpio
      ? "Escribe un nombre para continuar."
      : esNuevo
        ? "Después le asignas trabajadores desde Trabajadores."
        : "";

  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <div className="flex items-center gap-3">
          <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
            <Icon name="badge" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <FormDrawerTitle>{esNuevo ? "Nuevo cargo" : "Editar cargo"}</FormDrawerTitle>
            <FormDrawerDescription>
              {esNuevo
                ? "Los cargos ordenan a tu gente y deciden qué capacitaciones le tocan."
                : `${plural(cargo?.personas_count ?? 0, "trabajador", "trabajadores")} en este cargo`}
            </FormDrawerDescription>
          </div>
        </div>
      </FormDrawerHeader>

      <form onSubmit={submit} className={formDrawerBodyClass}>
        <div className="space-y-2" data-tour="cargo-nombre">
          <Label htmlFor="cargo-nombre" className="block">
            Nombre del cargo
          </Label>
          <Input
            id="cargo-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Operador de grúa horquilla"
            disabled={isSubmitting}
            // El choque BLOQUEA el guardado, así que va en rojo. El ámbar del DS
            // es para lo que avisa y deja seguir — mezclar los dos deja un borde
            // rojo con texto ámbar.
            aria-invalid={choque !== null}
            aria-describedby={choque ? "cargo-nombre-error" : undefined}
            autoFocus
          />
          {choque && (
            <p
              id="cargo-nombre-error"
              className="text-destructive flex items-center gap-1.5 text-[11.5px]"
            >
              <Icon name="error_outline" size={15} />
              {choque.enPapelera
                ? "Ya existe un cargo con ese nombre en la papelera. Restáuralo en vez de crear otro."
                : "Ya existe un cargo con ese nombre."}
            </p>
          )}
        </div>

        <div className="space-y-2" data-tour="cargo-descripcion">
          <Label htmlFor="cargo-descripcion" className="block">
            Qué hace en la práctica
          </Label>
          <Textarea
            id="cargo-descripcion"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Describe la tarea real del cargo. Sirve para proponerle los programas de capacitación y el EPP correctos."
            disabled={isSubmitting}
            rows={3}
          />
        </div>

        <div className="space-y-2" data-tour="cargo-areas">
          <div className="flex items-baseline justify-between gap-3">
            <Label className="block">Áreas habituales</Label>
            <span className="text-soft text-[11px]">Sugiere el área al sumar un trabajador</span>
          </div>
          {areas.length === 0 ? (
            <p className="text-muted-foreground text-[12.5px]">
              Todavía no hay áreas creadas. Es opcional: puedes definir el cargo igual.
            </p>
          ) : (
            <ChipsSeleccion
              aria-label="Áreas habituales del cargo"
              opciones={areas.map((a) => ({ id: a.id, etiqueta: formatNombreArea(a) }))}
              seleccionados={areasIds}
              onCambiar={setAreasIds}
              disabled={isSubmitting}
            />
          )}
        </div>

        {!esNuevo && (cargo?.personas_count ?? 0) > 0 && (
          <div className="space-y-2">
            <Label className="block">Trabajadores en el cargo</Label>
            {roster === null ? (
              <Loader2 className="text-soft h-4 w-4 animate-spin" />
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                {trabajadores.slice(0, AVATARES_VISIBLES).map((t) => (
                  // El span no es decorativo: `Tip` usa `asChild` de Radix, que
                  // necesita un elemento del DOM al que colgar el ref y los
                  // handlers. `AvatarIniciales` es un componente que no los
                  // reenvía, así que sin el span el tooltip nunca abriría.
                  <Tip key={t.uuid} texto={`${t.nombre} ${t.apellido}`}>
                    <span className="inline-flex">
                      <AvatarIniciales
                        iniciales={t.iniciales}
                        tamano="listado"
                        nombre={`${t.nombre} ${t.apellido}`}
                      />
                    </span>
                  </Tip>
                ))}
                {trabajadores.length > AVATARES_VISIBLES && (
                  <span className="bg-muted text-muted-foreground fc-num flex h-[30px] items-center rounded-full px-2.5 text-[11.5px]">
                    +{trabajadores.length - AVATARES_VISIBLES}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <FormDrawerFooter>
          {onPedirBorrar && (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              disabled={isSubmitting}
              onClick={onPedirBorrar}
            >
              <Icon name="delete" size={16} />
              Eliminar
            </Button>
          )}
          {/* Se renderiza aunque esté vacío: su `flex-1` es lo que separa
              Eliminar de Cancelar/Guardar. Sin él, con el formulario ya válido
              los tres botones quedarían pegados a la derecha. */}
          <FormDrawerFooterHint>{hint}</FormDrawerFooterHint>
          <FormDrawerClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              Cancelar
            </Button>
          </FormDrawerClose>
          {/* Sin `disabled` cuando falta algo: el botón se ve inerte pero sigue
              siendo clickeable, y el hint del pie dice qué falta. Un botón
              muerto no explica nada. */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className={puedeGuardar ? "gap-2" : "gap-2 opacity-50"}
            data-tour="cargo-guardar"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : esNuevo ? (
              <Plus className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {esNuevo ? "Crear cargo" : "Guardar cambios"}
          </Button>
        </FormDrawerFooter>
      </form>
    </FormDrawerContent>
  );
}
